import React from "react";

type AudioEvents = {
    onEnded?: () => void;
    onPlay?: () => void;
    onError?: (e: Event) => void;
    onStopped?: () => void;
    playbackRate?: number;
};

interface AudioPlayerContextType {
    playAudio: (blob: Blob, events?: AudioEvents) => () => void;
    stopAudio: () => void;
    isPlaying: boolean;
}

const AudioPlayerContext = React.createContext<AudioPlayerContextType | undefined>(undefined);

// share code
// also prevent audio from playing over each other

export const AudioPlayerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const currentAudioRef = React.useRef<HTMLAudioElement | null>(null);
    const stopCurrentRef = React.useRef<(() => void) | null>(null);
    const currentObjectUrlRef = React.useRef<string | null>(null);
    const playbackTimeoutRef = React.useRef<number | null>(null);
    const [isPlaying, setIsPlaying] = React.useState(false);
    // const [currentAudioId, setCurrentAudioId] = React.useState<number | null>(null);

    const clearPlaybackTimeout = React.useCallback(() => {
        if (playbackTimeoutRef.current !== null) {
            window.clearTimeout(playbackTimeoutRef.current);
            playbackTimeoutRef.current = null;
        }
    }, []);

    const clearCurrentObjectUrl = React.useCallback(() => {
        if (currentObjectUrlRef.current) {
            URL.revokeObjectURL(currentObjectUrlRef.current);
            currentObjectUrlRef.current = null;
        }
    }, []);

    const finishAudio = React.useCallback((audio: HTMLAudioElement) => {
        if (currentAudioRef.current !== audio) {
            return;
        }
        clearPlaybackTimeout();
        currentAudioRef.current = null;
        stopCurrentRef.current = null;
        clearCurrentObjectUrl();
        setIsPlaying(false);
    }, [clearPlaybackTimeout, clearCurrentObjectUrl]);

    const playAudio = React.useCallback((blob: Blob, events?: AudioEvents) => {
        if (currentAudioRef.current) {
            currentAudioRef.current.pause();
            currentAudioRef.current.currentTime = 0;
            stopCurrentRef.current?.();
        }
        clearPlaybackTimeout();
        clearCurrentObjectUrl();

        const objectUrl = URL.createObjectURL(blob);
        currentObjectUrlRef.current = objectUrl;
        const audio = new Audio(objectUrl);
        if (events?.playbackRate) audio.playbackRate = events.playbackRate;
        currentAudioRef.current = audio;
        setIsPlaying(true);
        const finish = () => {
            if (currentAudioRef.current !== audio) return;
            finishAudio(audio);
            events?.onStopped?.();
        };
        stopCurrentRef.current = finish;

        // Bind custom event listeners if provided
        if (events && events.onEnded) audio.addEventListener('ended', events.onEnded);
        if (events && events.onPlay) audio.addEventListener('play', events.onPlay);
        if (events && events.onError) audio.addEventListener('error', event => {
            if (currentAudioRef.current === audio) events.onError?.(event);
        });

        // Always reset state after playback ends or errors
        audio.addEventListener('ended', () => {
            finish();
        });
        audio.addEventListener('error', () => {
            finish();
        });
        audio.addEventListener('abort', () => {
            finish();
        });

        let hasStarted = false;
        const schedulePlaybackFallback = () => {
            if (currentAudioRef.current !== audio) return;
            clearPlaybackTimeout();
            // A system interruption can pause media without ending it. Resume
            // scheduling only when playback continues.
            if (hasStarted && audio.paused) return;
            // A MediaRecorder webm may report Infinity for its whole duration.
            // Watch for stalled progress, not total elapsed time, so a long
            // student recording can play through to its real ended event.
            const knownDuration = Number.isFinite(audio.duration) && audio.duration > 0;
            const remainingMs = knownDuration
                ? Math.max(0, audio.duration - audio.currentTime) * 1000 / audio.playbackRate
                : null;
            const timeoutMs = remainingMs === null
                ? audio.currentTime > 0 ? 15_000 : 30_000
                : Math.max(remainingMs + 2_000, 5_000);
            playbackTimeoutRef.current = window.setTimeout(() => {
                if (currentAudioRef.current !== audio) return;
                if (Number.isFinite(audio.duration) && audio.currentTime >= audio.duration - 0.25) {
                    events?.onEnded?.();
                } else {
                    events?.onError?.(new Event('error'));
                }
                audio.pause();
                finish();
            }, timeoutMs);
        };

        // Also cover a stalled load: metadata and play() may never settle.
        schedulePlaybackFallback();
        audio.addEventListener('loadedmetadata', schedulePlaybackFallback, { once: true });
        audio.addEventListener('durationchange', schedulePlaybackFallback);
        audio.addEventListener('timeupdate', schedulePlaybackFallback);
        audio.addEventListener('pause', () => {
            if (currentAudioRef.current !== audio || !hasStarted) return;
            clearPlaybackTimeout();
            setIsPlaying(false);
        });
        audio.addEventListener('playing', () => {
            if (currentAudioRef.current !== audio) return;
            hasStarted = true;
            setIsPlaying(true);
            schedulePlaybackFallback();
        });

        audio.play()
            .then(() => {
                if (currentAudioRef.current !== audio) return;
                hasStarted = true;
                schedulePlaybackFallback();
            })
            .catch(() => {
                if (currentAudioRef.current !== audio) return;
                events?.onError?.(new Event('error'));
                finish();
            });
        return () => {
            if (currentAudioRef.current !== audio) return;
            audio.pause();
            audio.currentTime = 0;
            finish();
        };
    }, [finishAudio, clearPlaybackTimeout, clearCurrentObjectUrl]);

    const stopAudio = React.useCallback(() => {
        if (currentAudioRef.current) {
            currentAudioRef.current.pause();
            currentAudioRef.current.currentTime = 0;
            stopCurrentRef.current?.();
        }
        clearPlaybackTimeout();
        clearCurrentObjectUrl();
        setIsPlaying(false);
    }, [clearPlaybackTimeout, clearCurrentObjectUrl]);

    React.useEffect(() => () => {
        currentAudioRef.current?.pause();
        currentAudioRef.current = null;
        stopCurrentRef.current = null;
        clearPlaybackTimeout();
        clearCurrentObjectUrl();
    }, [clearPlaybackTimeout, clearCurrentObjectUrl]);

    return (
        <AudioPlayerContext.Provider value={{ playAudio, stopAudio, isPlaying }}>
            {children}
        </AudioPlayerContext.Provider>
    );
};

export const useAudioPlayer = (): AudioPlayerContextType => {
    const context = React.useContext(AudioPlayerContext);
    if (!context) {
        throw new Error('useAudioPlayer must be used within an AudioPlayerProvider');
    }
    return context;
};
